import { HintButton } from "./HintButton.js";
import styles from "./DuelEngine.module.css";

export interface DuelOption {
  id: number;
}

export interface DuelHintProps {
  hintedIds: Set<number>;
  onHint: (id: number) => void;
  getHintLabel: string;
  viewHintLabel: string;
}

export interface DuelEngineProps {
  optionA: DuelOption & { label: string };
  optionB: DuelOption & { label: string };
  onChoose: (chosenId: number) => void;
  disabled?: boolean;
  prompt: string;
  /** Shown prominently between the prompt and the two options (e.g. the
      reference city a "which is nearer to…" prompt continues into). */
  subject?: string | undefined;
  /** Once answered: which option was picked, and whether it was correct — highlights it. */
  selectedId?: number | null | undefined;
  selectedCorrect?: boolean | undefined;
  /** Optional per-option "give hint" button (Beeline R3 only). */
  hint?: DuelHintProps | undefined;
}

function optionClassName(
  id: number,
  selectedId: number | null | undefined,
  selectedCorrect: boolean | undefined,
): string {
  const base: string = styles["option"] ?? "";
  if (selectedId === id) {
    const extra = selectedCorrect ? styles["correct"] : styles["incorrect"];
    return `${base} ${extra ?? ""}`;
  }
  return base;
}

/** Reusable near-or-far-style duel: two options, pick one. */
export function DuelEngine({
  optionA,
  optionB,
  onChoose,
  disabled = false,
  prompt,
  subject,
  selectedId = null,
  selectedCorrect,
  hint,
}: DuelEngineProps) {
  return (
    <div className={styles.wrapper}>
      <p className={styles.prompt}>{prompt}</p>
      {subject && <p className={styles.subject}>{subject}</p>}
      <div className={styles.options}>
        <div className={styles.optionWrap}>
          <button
            type="button"
            className={optionClassName(optionA.id, selectedId, selectedCorrect)}
            disabled={disabled}
            onClick={() => onChoose(optionA.id)}
          >
            {optionA.label}
          </button>
          {hint && (
            <div className={styles.hintSlot}>
              <HintButton
                revealed={hint.hintedIds.has(optionA.id)}
                onClick={() => hint.onHint(optionA.id)}
                getHintLabel={hint.getHintLabel}
                viewHintLabel={hint.viewHintLabel}
              />
            </div>
          )}
        </div>
        <span className={styles.vs}>·</span>
        <div className={styles.optionWrap}>
          <button
            type="button"
            className={optionClassName(optionB.id, selectedId, selectedCorrect)}
            disabled={disabled}
            onClick={() => onChoose(optionB.id)}
          >
            {optionB.label}
          </button>
          {hint && (
            <div className={styles.hintSlot}>
              <HintButton
                revealed={hint.hintedIds.has(optionB.id)}
                onClick={() => hint.onHint(optionB.id)}
                getHintLabel={hint.getHintLabel}
                viewHintLabel={hint.viewHintLabel}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
